import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useOutletContext } from 'react-router'

import {
  diseno as accionesDe,
  useEstadoDelDiseno,
  useGeneracion,
  useHistorial,
  usePaginas,
  useRefrescarDiseno,
  type Estimacion,
} from '../api/consultas'
import type { PeticionDeDiseno } from '../api/idiomas'
import { consulta, ErrorDeLaApi, pedir } from '../api/nucleo'
import type { PublicacionDeLaWeb } from '../api/publicacion'
import type { Permisos } from '../api/capacidades'
import type { Alcance, Cambio, EstadoDeGeneracion, RespuestaDeGeneracion, Sitio, VersionEnHistorial } from '../api/tipos'
import { Eye, Sparkles } from 'lucide-react'

import { Aviso, Boton, Esqueleto, Selector, Tarjeta, Titulo, claseDeEntrada, clases } from '../componentes/ui'
import { CajaDeConflicto, Confirmacion } from './diseno/Conflicto'
import { ComponentesDelDiseno } from './diseno/Componentes'
import { HistorialDeDiseno } from './diseno/Historial'
import { DialogoDePublicacion } from '../publicar/DialogoDePublicacion'
import { enQuePlan, nombreDeFichero, queLePaso, sinCreditos } from './diseno/textos'
import { faltaLaPortada, fraseDePaginas, portadaEnBorrador, SIN_PAGINA_DE_INICIO } from './primeraPublicacion'

type Contexto = { espacio: number; permisos: Permisos; saldo: null | number; sitio: null | Sitio }

type Catalogo = {
  alcances: Alcance[]
  bloques: Array<{ etiqueta: string; slug: string }>
  congelado: boolean
  permiso: 'equipo' | 'ninguno' | 'proponer' | 'publicar'
  /** De cada alcance que el plan no incluye, en qué planes está. Opcional para no romper con un núcleo de antes. */
  enQuePlanes?: Partial<Record<Alcance, string[]>>
}

const ALCANCE: Record<Alcance, { ayuda: string; etiqueta: string }> = {
  seccion: { etiqueta: 'Esta sección', ayuda: 'Un bloque: el hero, los testimonios, el pie…' },
  pagina: { etiqueta: 'Esta página', ayuda: 'Los bloques de una página y su plantilla.' },
  sitio: { etiqueta: 'Todo el sitio', ayuda: 'Colores, tipografías, espaciado y layouts.' },
}

type Fase = 'confirmar' | 'construyendo' | 'estimando' | 'generando' | 'lista' | 'previa'
type Mensaje = { enlace?: { etiqueta: string; href: string }; texto: string; tono: 'aviso' | 'error' | 'ok' }

/**
 * «Diseño con IA» (CLAUDE.md §20). El ciclo es el de siempre —estimar, confirmar lo
 * que cuesta, generar, esperar a que construya, previa, y después descartar · seguir
 * modificando · publicar— y **ninguna regla vive aquí**: qué alcances hay, si se puede
 * publicar o hay que solicitarlo, si hay saldo y si el diseño está congelado lo dice
 * el núcleo, y lo que responde se enseña con sus palabras (§28).
 */
export const Diseno = () => {
  const { espacio, permisos, sitio, saldo } = useOutletContext<Contexto>()
  if (!sitio) return <Aviso tono="error">Esa web no existe, o no es tuya.</Aviso>
  return <Pantalla espacio={espacio} key={sitio.id} permisos={permisos} saldo={saldo} sitio={sitio} />
}

const Pantalla = ({ espacio, permisos, saldo, sitio }: { espacio: number; permisos: Permisos; saldo: null | number; sitio: Sitio }) => {
  const acciones = accionesDe(sitio.id)
  const refrescar = useRefrescarDiseno(sitio.id, espacio)

  const { data: catalogo, isPending: cargandoCatalogo } = useQuery({
    queryKey: ['catalogo', sitio.id],
    queryFn: () => pedir<Catalogo>(`/panel/catalogo${consulta({ sitio: sitio.id })}`),
  })
  const { data: estado } = useEstadoDelDiseno(sitio.id)
  const { data: paginas } = usePaginas(sitio.id)

  // Lo que llega ya escrito desde otra pantalla (Idiomas: pasar a sus textos los componentes en español, §37): la petición,
  // «todo el sitio» y los ficheros concretos. Se ve y se puede cambiar antes de generar.
  const llegada = (useLocation().state as null | { peticion?: PeticionDeDiseno })?.peticion ?? null
  const [fase, setFase] = useState<Fase>('lista')
  const [prompt, setPrompt] = useState(llegada?.prompt ?? '')
  const [alcance, setAlcance] = useState<Alcance>(llegada?.alcance ?? 'seccion')
  const [rutas, setRutas] = useState<string[]>(llegada?.rutas ?? [])
  const [bloque, setBloque] = useState('hero')
  const [pagina, setPagina] = useState<null | number>(null)
  const [mensaje, setMensaje] = useState<Mensaje | null>(null)
  const [estimacion, setEstimacion] = useState<Estimacion | null>(null)
  const [resultado, setResultado] = useState<null | RespuestaDeGeneracion>(null)
  const [versionActual, setVersionActual] = useState<null | number>(null)
  const [previaUrl, setPreviaUrl] = useState<null | string>(null)
  const [comparacion, setComparacion] = useState<null | { cambios: Cambio[]; desde: number; hasta: number }>(null)
  const [solicitada, setSolicitada] = useState<null | number>(null)
  const [ocupado, setOcupado] = useState(false)
  // La generación en cola que se está esperando (su número), y sobre qué versión se pide lo siguiente
  // cuando se vuelve a pedir un cambio sobre lo publicado (§18).
  const [generacion, setGeneracion] = useState<null | number>(null)
  const [desde, setDesde] = useState<null | number>(null)
  const [confirmar, setConfirmar] = useState<null | 'revertir'>(null)
  const resuelta = useRef<null | number>(null)
  const areaDeTexto = useRef<HTMLTextAreaElement>(null)
  // «Publicar mi web» (§13): `diseno` publica (o solicita) esta versión, con el contenido la
  // primera vez; `contenido` solo publica las páginas (desde la previa vacía). El diálogo pide
  // la confirmación y dice qué salió y qué no.
  const [dialogo, setDialogo] = useState<null | 'contenido' | 'diseno'>(null)
  // Para volver a cargar la previa cuando cambia lo publicado: mismo enlace, otro `key`.
  const [recargas, setRecargas] = useState(0)

  // Mientras construye, el historial se refresca solo: es también el sondeo. Lo lee el núcleo para
  // quien diseña (`/historial`): con el listado de la colección, un editor esperaba para siempre.
  const esperando = fase === 'construyendo' && versionActual !== null
  const { data: historial, refetch: releerHistorial } = useHistorial(sitio.id, { refrescarCada: esperando ? 3000 : false, activo: Boolean(catalogo) })
  const versiones = historial?.versiones
  // La generación en cola: se sondea mientras está en curso (api/consultas.ts).
  const { data: enCurso } = useGeneracion(sitio.id, generacion)

  const publicada = estado?.publicada ?? 0
  const flujo = estado?.flujo ?? 'aprobacion'
  const permiso = catalogo?.permiso ?? 'ninguno'
  const puedePublicar = permiso === 'equipo' || (permiso === 'publicar' && flujo === 'directa')
  const trabajando = fase === 'estimando' || fase === 'generando' || fase === 'construyendo'
  // Otra persona de la cuenta está generando: van de una en una, así que se espera a que termine.
  const deOtro = historial?.enMarcha && !historial.enMarcha.tuya && generacion === null ? historial.enMarcha : null
  const puedeGenerar = !catalogo?.congelado && !ocupado && !trabajando && permiso !== 'ninguno' && !deOtro
  const actual: undefined | VersionEnHistorial = versiones?.find((v) => v.numero === versionActual)
  const laPublicada = versiones?.find((v) => v.numero === publicada)
  // Publicarla pisaría lo publicado (§18): en vez de «Publicar», la caja del conflicto.
  const enConflicto = Boolean(actual && publicada > 0 && actual.numero !== publicada && !actual.parteDeLaPublicada)

  useEffect(() => {
    if (catalogo?.alcances?.length && !catalogo.alcances.includes(alcance)) setAlcance(catalogo.alcances[0] as Alcance)
    if (catalogo?.bloques?.length && !catalogo.bloques.some((b) => b.slug === bloque)) setBloque(catalogo.bloques[0]!.slug)
  }, [catalogo, alcance, bloque])
  useEffect(() => {
    if (pagina === null && paginas?.length) setPagina(paginas[0]!.id)
  }, [paginas, pagina])

  const fallo = (e: unknown) => {
    const error = e as ErrorDeLaApi
    // «Eso es contenido» lleva a las páginas de esta web, en este panel. La dirección que trae el
    // núcleo es la del panel del equipo, que el cliente no ve.
    const enlace = error?.datos?.esContenido ? { href: `/e/${espacio}/s/${sitio.id}/paginas`, etiqueta: 'Ir a Páginas' } : undefined
    setMensaje({ texto: error?.message || 'Algo no ha ido bien. Vuelve a intentarlo.', tono: 'error', enlace })
    setOcupado(false)
  }

  const abrirPrevia = async (numero: number) => {
    try {
      const r = await acciones.previa(numero)
      setPreviaUrl(r.url)
      setVersionActual(numero)
      setFase('previa')
      refrescar()
    } catch (e) {
      setFase('lista')
      fallo(e)
    }
  }

  // Cuando la versión que se espera termina de construirse, se abre su previa sola.
  useEffect(() => {
    if (!esperando || !versiones) return
    const suya = versiones.find((v) => v.numero === versionActual)
    if (!suya) return
    if (suya.construida) void abrirPrevia(suya.numero)
    else if (suya.estado === 'fallida') {
      setFase('lista')
      setMensaje({
        texto: `No se ha podido preparar este cambio. Lo que está publicado no se ha tocado${suya.ia && suya.creditos === 0 ? ' y no se te ha cobrado: te hemos devuelto los créditos' : ''}. Lo siguiente que pidas ya no parte de él: prueba a pedirlo de otra forma, o descártalo en el historial.`,
        tono: 'error',
      })
      refrescar()
    } else if (suya.estado === 'borrador') {
      // Nadie la va a construir: la cola no la aceptó. Se dice, con el botón para prepararla.
      setFase('lista')
      setMensaje({ texto: 'Tu cambio está hecho, pero su previa no se ha podido preparar todavía. Pulsa «Preparar la previa» en el historial.', tono: 'aviso' })
    }
    // `abrirPrevia` cambia de identidad en cada render y no debe relanzar el efecto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versiones, esperando, versionActual])

  /** Lo que respondió una generación: su versión pasa a construirse, y la pantalla espera su previa. */
  const aplicar = (r: RespuestaDeGeneracion) => {
    setResultado(r)
    setVersionActual(r.version)
    setSolicitada(null)
    setDesde(null)
    refrescar()
    if (r.aviso) {
      setFase('lista')
      setMensaje({ texto: r.aviso, tono: 'aviso' })
      return
    }
    if (r.conflicto) {
      setMensaje({
        texto: 'Mientras se generaba se guardó otra versión. Tu cambio está hecho: antes de publicarlo te diremos si pisa algo.',
        tono: 'aviso',
      })
    }
    setFase('construyendo')
  }

  /** Cómo va la generación en cola: lista, fallida o todavía en curso (entonces se sigue sondeando). */
  const seguir = (g: EstadoDeGeneracion) => {
    if (g.estado === 'en-curso') {
      setGeneracion(g.generacion)
      setFase('generando')
      return
    }
    if (resuelta.current === g.generacion) return
    resuelta.current = g.generacion
    setGeneracion(null)
    if (g.estado === 'lista' && g.respuesta) return aplicar(g.respuesta)
    setFase('lista')
    refrescar()
    const enlace = g.datos?.esContenido ? { href: `/e/${espacio}/s/${sitio.id}/paginas`, etiqueta: 'Ir a Páginas' } : undefined
    setMensaje({ texto: g.mensaje || 'No se ha podido generar. No se te ha cobrado nada: vuelve a intentarlo.', tono: 'error', enlace })
  }

  useEffect(() => {
    if (enCurso && enCurso.generacion === generacion) seguir(enCurso)
    // `seguir` cambia de identidad en cada render y no debe relanzar el efecto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enCurso, generacion])

  // Al volver a la pantalla con una generación suya en marcha (recargó, o se fue y volvió), se
  // sigue esperando en vez de ofrecer otra: el núcleo la rechazaría, y así no se paga dos veces.
  const reanudada = useRef(false)
  useEffect(() => {
    if (reanudada.current || !historial?.enMarcha?.tuya || fase !== 'lista' || generacion !== null) return
    reanudada.current = true
    const { enMarcha } = historial
    setPrompt((antes) => antes || enMarcha.prompt)
    setGeneracion(enMarcha.generacion)
    setFase('generando')
    setMensaje({ texto: 'Tienes un cambio generándose: seguimos esperándolo. Puedes quedarte o volver más tarde; aparecerá en el historial.', tono: 'ok' })
  }, [historial, fase, generacion])

  const objetivo = () => ({
    prompt: prompt.trim(),
    alcance,
    ...(alcance === 'seccion' ? { bloque } : {}),
    ...(alcance === 'pagina' && pagina ? { pagina } : {}),
    ...(alcance === 'sitio' && rutas.length > 0 ? { rutas } : {}),
    ...(desde !== null ? { desde } : {}),
  })

  const estimar = async () => {
    setMensaje(null)
    setComparacion(null)
    if (prompt.trim().length < 8) {
      return setMensaje({
        texto: 'Escribe qué quieres cambiar con un poco de detalle: por ejemplo «el hero con fondo oscuro y el título a la izquierda».',
        tono: 'aviso',
      })
    }
    setOcupado(true)
    setFase('estimando')
    try {
      const e = await acciones.estimar(objetivo())
      setEstimacion(e)
      setFase('confirmar')
    } catch (e) {
      setFase('lista')
      fallo(e)
    } finally {
      setOcupado(false)
    }
  }

  /**
   * Generar **en cola** (`enCola`): el núcleo responde enseguida y la pantalla sigue la
   * generación. Esperar la respuesta entera pasaba de los 100 s de Cloudflare en «esta página»
   * y «todo el sitio»: el cliente leía un error, se le cobraba, y si repetía pagaba dos veces.
   */
  const generar = async () => {
    setMensaje(null)
    setPreviaUrl(null)
    setOcupado(true)
    setFase('generando')
    try {
      seguir(await acciones.generarEnCola(objetivo()))
    } catch (e) {
      const error = e as ErrorDeLaApi
      // Un corte, un 5xx o «ya hay una en marcha»: puede que la generación exista. Antes de
      // invitar a repetir (y pagar otra vez) se mira el historial, y si está en marcha se espera.
      if (!(error instanceof ErrorDeLaApi) || error.status >= 500 || error.status === 409) {
        const { data: ahora } = await releerHistorial()
        refrescar()
        if (ahora?.enMarcha?.tuya) {
          setGeneracion(ahora.enMarcha.generacion)
          setMensaje({ texto: 'Tu cambio ya se está generando: lo esperamos aquí. No hace falta pedirlo otra vez.', tono: 'ok' })
          return
        }
        setFase('lista')
        setMensaje({
          texto:
            error instanceof ErrorDeLaApi && error.status === 409
              ? error.message
              : 'No hemos recibido respuesta del servidor. Puede que tu cambio se haya creado igualmente: mira el historial antes de pedirlo otra vez, para no pagarlo dos veces.',
          tono: 'aviso',
        })
        return
      }
      setFase('lista')
      fallo(e)
    } finally {
      setOcupado(false)
    }
  }

  const descartarVersion = async (numero: number) => {
    setOcupado(true)
    try {
      await acciones.descartar(numero)
      setMensaje({ texto: 'Descartado: sigue en el historial, pero lo siguiente que pidas no parte de ahí.', tono: 'ok' })
      if (numero === versionActual) {
        setPreviaUrl(null)
        setResultado(null)
        setVersionActual(null)
        setFase('lista')
      }
      refrescar()
    } catch (e) {
      fallo(e)
    } finally {
      setOcupado(false)
    }
  }
  const descartar = () => (versionActual ? descartarVersion(versionActual) : undefined)

  /** Preparar la previa de una versión que se quedó sin construir (la cola no la aceptó). */
  const construirVersion = async (numero: number) => {
    setMensaje(null)
    setOcupado(true)
    try {
      await acciones.construir(numero)
      setResultado(null)
      setVersionActual(numero)
      setFase('construyendo')
      refrescar()
    } catch (e) {
      fallo(e)
    } finally {
      setOcupado(false)
    }
  }

  /** Juntar la versión con la publicada (§18): solo si tocan partes distintas; si no, el núcleo dice cuáles coinciden. */
  const juntar = async () => {
    if (!versionActual || !publicada) return
    setMensaje(null)
    setOcupado(true)
    try {
      const junta = await acciones.fusionar(versionActual, publicada)
      await acciones.construir(junta.version)
      setResultado(null)
      setPreviaUrl(null)
      setVersionActual(junta.version)
      setFase('construyendo')
      setMensaje({ texto: 'Hemos juntado tu cambio con lo publicado. Cuando esté lista la previa, compruébalo y publícalo.', tono: 'ok' })
      refrescar()
    } catch (e) {
      fallo(e)
    } finally {
      setOcupado(false)
    }
  }

  /** Volver a pedir el mismo cambio sobre lo publicado: cuesta créditos, así que pasa por la estimación. */
  const regenerarSobreLaPublicada = () => {
    if (actual?.prompt) setPrompt(actual.prompt)
    setDesde(publicada)
    setPreviaUrl(null)
    setFase('lista')
    setMensaje({ texto: 'Pulsa «Generar» para pedir tu cambio otra vez, esta vez sobre lo publicado. Antes de gastar nada te decimos cuánto cuesta.', tono: 'ok' })
    areaDeTexto.current?.focus()
  }

  const volverALaAnterior = async () => {
    setOcupado(true)
    try {
      await acciones.revertir()
      setConfirmar(null)
      setMensaje({ texto: 'Tu web ha vuelto a la versión anterior. Ya se ve así en internet.', tono: 'ok' })
      setRecargas((n) => n + 1)
      refrescar()
    } catch (e) {
      setConfirmar(null)
      fallo(e)
    } finally {
      setOcupado(false)
    }
  }

  const contenido = estado?.contenido ?? null
  const sinPortada = faltaLaPortada(contenido)

  /**
   * Lo que pasa en esta pantalla cuando termina «Publicar mi web»: se refresca todo, se vuelve
   * a cargar la previa y se dice en llano qué ha pasado con el diseño. Un conflicto del §18
   * llega como `motivo`: el núcleo publica el diseño por la misma puerta que antes.
   */
  const alTerminarLaPublicacion = (r: PublicacionDeLaWeb) => {
    refrescar()
    setRecargas((n) => n + 1)
    if (r.diseno?.accion === 'solicitado' && r.diseno.version) setSolicitada(r.diseno.version)
    if (r.diseno?.accion === 'publicado') setMensaje({ texto: 'Publicado. Ya se ve en tu web; si algo no te convence, puedes volver a la versión anterior desde el historial.', tono: 'ok' })
    else if (r.diseno?.accion === 'solicitado') setMensaje({ texto: 'Solicitud enviada: el equipo la revisa y te llega un correo cuando la resuelva. Aquí verás cómo va.', tono: 'ok' })
    else if (r.diseno?.motivo) setMensaje({ texto: r.diseno.motivo, tono: 'error' })
  }

  const restaurar = async (v: VersionEnHistorial) => {
    if (!window.confirm(`¿Volver a partir de la versión ${v.numero} («${v.comentario}»)? Se crea una versión nueva igual que ella, con su previa; lo publicado no cambia hasta que la publiques.`)) return
    setMensaje(null)
    setOcupado(true)
    try {
      const nueva = await acciones.restaurar(v.numero)
      await acciones.construir(nueva.version)
      setResultado(null)
      setVersionActual(nueva.version)
      setFase('construyendo')
      refrescar()
    } catch (e) {
      setFase('lista')
      fallo(e)
    } finally {
      setOcupado(false)
    }
  }

  /** Con lo publicado si es otra; si no, con aquella de la que partió. */
  const comparar = async (v: Pick<VersionEnHistorial, 'numero' | 'padre'>) => {
    setMensaje(null)
    const contra = publicada && publicada !== v.numero ? publicada : Math.max(0, v.padre)
    try {
      const r = await acciones.comparar(contra, v.numero)
      setComparacion({ desde: contra, hasta: v.numero, cambios: r.cambios })
    } catch (e) {
      fallo(e)
    }
  }

  /** «Publicar» o «Solicitar publicación»: lo lleva el diálogo de «Publicar mi web», que pide la confirmación. */
  const pedirPublicar = () => setDialogo('diseno')

  const accionesDelHistorial = {
    previa: (numero: number) => void abrirPrevia(numero),
    comparar: (v: VersionEnHistorial) => void comparar(v),
    restaurar: (v: VersionEnHistorial) => void restaurar(v),
    descartar: (v: VersionEnHistorial) => void descartarVersion(v.numero),
    construir: (v: VersionEnHistorial) => void construirVersion(v.numero),
    volverALaAnterior: () => setConfirmar('revertir'),
  }
  const bloques = catalogo?.bloques ?? []
  // Quién puede arreglar lo que falta: los permisos, quien edita a las personas de la cuenta; el plan y los créditos, quien los compra.
  const administraPersonas = permisos.tiene('usuarios.editar')
  const puedeComprar = permisos.tiene('creditos.comprar')
  const faltanCreditos = sinCreditos(puedeComprar)

  const tarjetaDeComparacion = comparacion && (
    <Tarjeta className="mb-6">
      <div className="flex items-center justify-between gap-4">
        <span className="text-sm">
          {comparacion.desde === 0 ? 'Del tema de partida' : `De la versión ${comparacion.desde}`} a la versión {comparacion.hasta}
          {comparacion.desde === publicada ? ' (la publicada)' : ''}:{' '}
          {comparacion.cambios.length === 0 ? 'son iguales.' : comparacion.cambios.length === 1 ? 'cambia una parte.' : `cambian ${comparacion.cambios.length} partes.`}
        </span>
        <Boton onClick={() => setComparacion(null)} pequeno>
          Cerrar
        </Boton>
      </div>
      <ul className="mt-2 flex flex-col gap-0.5 text-sm">
        {comparacion.cambios.map((c) => (
          <li key={c.ruta}>
            {nombreDeFichero(c.ruta, bloques)} <span className="text-tenue">({queLePaso(c.estado)})</span>
          </li>
        ))}
      </ul>
    </Tarjeta>
  )

  if (cargandoCatalogo) return <Esqueleto filas={8} />
  if (permiso === 'ninguno') {
    // Quien solo ve el diseño mira la historia de su web; nadie más ve nada aquí.
    return (
      <>
        <Titulo rotulo={sitio.nombre}>Diseño con IA</Titulo>
        <div className="mb-6">
          <Aviso>
            {administraPersonas
              ? 'Tu usuario no tiene permiso para cambiar el diseño. Aquí ves su historia; para diseñar con IA, marca «Cambiar el diseño con IA» en tu ficha, en «Usuarios».'
              : 'No tienes permiso para cambiar el diseño de esta web. Si lo necesitas, pídeselo al propietario de la cuenta.'}
          </Aviso>
        </div>
        {tarjetaDeComparacion}
        {historial && <HistorialDeDiseno acciones={accionesDelHistorial} bloques={bloques} congelado={Boolean(catalogo?.congelado)} historial={historial} ocupado={ocupado} versionActual={null} />}
      </>
    )
  }

  return (
    <>
      <Titulo
        accion={
          <span className="inline-flex h-9 items-center gap-1.5 rounded-boton border border-linea bg-papel px-3 text-[13px]">
            <Sparkles className="size-3.5 text-violeta" />
            <span className="font-medium tabular-nums">{(saldo ?? 0).toLocaleString('es')}</span>
            <span className="text-tenue">créditos</span>
          </span>
        }
        descripcion="Escribe cómo quieres que se vea y un modelo escribe el código. Lo miras en la previa antes de publicarlo."
      >
        Diseño con IA
      </Titulo>

      {catalogo?.congelado && (
        <div className="mb-4">
          <Aviso>
            Tu equipo está trabajando en tu web ahora mismo y ha congelado el diseño para no pisaros. Mirar, puedes; cambiar, en cuanto
            terminen.
          </Aviso>
        </div>
      )}

      <div className="mb-8 grid gap-6 lg:grid-cols-[minmax(20rem,2fr)_minmax(0,3fr)]">
        <Tarjeta className="flex flex-col gap-5">
          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium">¿Qué quieres modificar?</span>
            <textarea
              className={clases(claseDeEntrada(), 'h-auto min-h-28 resize-y py-2')}
              disabled={!puedeGenerar}
              maxLength={2000}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="El hero con fondo oscuro, el título a la izquierda, una imagen grande a la derecha y el botón principal en amarillo."
              ref={areaDeTexto}
              value={prompt}
            />
          </label>

          <fieldset className="flex flex-col gap-2">
            <legend className="mb-2 text-[13px] font-medium">Modificar</legend>
            {(['seccion', 'pagina', 'sitio'] as Alcance[]).map((a) => {
              const permitido = catalogo?.alcances.includes(a) ?? false
              return (
                <label
                  className={clases('flex cursor-pointer items-start gap-3 rounded-caja border p-3 transition-colors', alcance === a ? 'border-violeta-borde bg-violeta-suave/50' : 'border-linea hover:border-linea-fuerte', !permitido && 'cursor-not-allowed opacity-60')}
                  key={a}
                >
                  <input
                    checked={alcance === a}
                    className="mt-1"
                    disabled={!permitido || !puedeGenerar}
                    name="alcance"
                    onChange={() => setAlcance(a)}
                    type="radio"
                  />
                  <span className="flex flex-col">
                    <strong>{ALCANCE[a].etiqueta}</strong>
                    <small className="text-tenue">
                      {permitido ? ALCANCE[a].ayuda : enQuePlan(catalogo?.enQuePlanes?.[a])}
                      {!permitido && puedeComprar && (
                        <>
                          {' '}
                          <Link className="font-medium text-violeta hover:underline" to={`/e/${espacio}/creditos#planes`}>
                            Ver los planes
                          </Link>
                        </>
                      )}
                    </small>
                  </span>
                </label>
              )
            })}
          </fieldset>

          {alcance === 'sitio' && rutas.length > 0 && (
            <div className="flex flex-col gap-1.5 text-sm">
              <span className="text-[13px] font-medium">Y estos componentes</span>
              <ul className="flex flex-wrap gap-1.5">
                {rutas.map((r) => (
                  <li key={r}>
                    <code className="rounded bg-papel-2 px-1.5 py-0.5">{r}</code>
                  </li>
                ))}
              </ul>
              <button className="self-start text-tenue underline" disabled={!puedeGenerar} onClick={() => setRutas([])} type="button">
                Quitarlos: solo tokens, estilos y cabecera y pie
              </button>
            </div>
          )}

          {alcance === 'seccion' && (
            <label className="flex flex-col gap-1.5">
              <span className="text-[13px] font-medium">Qué sección</span>
              <Selector
                disabled={!puedeGenerar}
                onChange={(e) => setBloque(e.target.value)}
                value={bloque}
              >
                {(catalogo?.bloques ?? []).map((b) => (
                  <option key={b.slug} value={b.slug}>
                    {b.etiqueta}
                  </option>
                ))}
              </Selector>
            </label>
          )}

          {alcance === 'pagina' && (
            <label className="flex flex-col gap-1.5">
              <span className="text-[13px] font-medium">Qué página</span>
              <Selector
                disabled={!puedeGenerar || !paginas?.length}
                onChange={(e) => setPagina(Number(e.target.value))}
                value={pagina ?? ''}
              >
                {!paginas?.length && <option value="">Esta web no tiene páginas todavía</option>}
                {(paginas ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.titulo}
                  </option>
                ))}
              </Selector>
            </label>
          )}

          {fase === 'confirmar' && estimacion ? (
            <div className="rounded-caja border border-violeta-borde bg-violeta-suave/50 p-4 text-sm">
              <p>
                Como mucho <strong>{estimacion.creditos}</strong> créditos: se cobra lo que cueste de verdad. Te quedan {estimacion.saldo}.{' '}
                {estimacion.alcanza ? 'Si no te convence el resultado, lo descartas; y si no se puede construir, no se cobra.' : faltanCreditos.texto}
                {!estimacion.alcanza && faltanCreditos.enlace && (
                  <>
                    {' '}
                    <Link className="font-medium text-violeta hover:underline" to={`/e/${espacio}/creditos`}>
                      {faltanCreditos.enlace}
                    </Link>
                  </>
                )}
              </p>
              {desde !== null && <p className="mt-2 text-[13px] text-tinta-2">Se pedirá sobre lo que está publicado ahora.</p>}
              <div className="mt-4 flex flex-wrap gap-2">
                <Boton disabled={!estimacion.alcanza || ocupado} onClick={generar} tipo="primario">
                  Generar ahora
                </Boton>
                <Boton onClick={() => setFase('lista')}>Cancelar</Boton>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <Boton disabled={!puedeGenerar} icono={<Sparkles />} onClick={estimar} tipo="primario">
                {fase === 'estimando' ? 'Calculando…' : fase === 'generando' ? 'Generando…' : fase === 'construyendo' ? 'Construyendo…' : 'Generar'}
              </Boton>
              {trabajando && (
                <span className="text-sm text-tenue" role="status">
                  {fase === 'estimando' && 'Calculando lo que va a costar.'}
                  {fase === 'generando' && 'El modelo está escribiendo el código. Puede tardar unos minutos: si te vas, lo encontrarás en el historial.'}
                  {fase === 'construyendo' && 'Preparando la previa: compilamos tu web aparte, sin tocar la publicada. Un par de minutos.'}
                </span>
              )}
            </div>
          )}

          {deOtro && fase === 'lista' && (
            <Aviso tono="info">Otra persona de tu cuenta está generando un cambio en esta web. Van de uno en uno: cuando termine, aparecerá en el historial y podrás pedir el tuyo.</Aviso>
          )}

          {mensaje && (
            <Aviso tono={mensaje.tono}>
              {mensaje.texto}
              {mensaje.enlace && (
                <>
                  {' '}
                  <Link className="underline" to={mensaje.enlace.href}>
                    {mensaje.enlace.etiqueta}
                  </Link>
                </>
              )}
            </Aviso>
          )}

          {resultado && versionActual === resultado.version && (
            <section className="border-t border-linea pt-4">
              <h2 className="font-display text-lg font-semibold">Tu cambio</h2>
              {resultado.explicacion && <p className="mt-1 text-tinta-2">{resultado.explicacion}</p>}
              {(resultado.cambios ?? []).length > 0 && (
                <ul className="mt-2 flex flex-col gap-0.5 text-sm">
                  {(resultado.cambios ?? []).map((c) => (
                    <li key={c.ruta}>
                      {nombreDeFichero(c.ruta, bloques)} <span className="text-tenue">({queLePaso(c.estado)})</span>
                    </li>
                  ))}
                </ul>
              )}
              {(resultado.noAplicado ?? []).map((n) => (
                <p className="mt-2 text-sm text-tenue" key={n.ruta}>
                  {nombreDeFichero(n.ruta, bloques)}: {n.motivo}
                </p>
              ))}
              {(resultado.accionesPropuestas ?? []).map((a, i) => (
                <p className="mt-2 text-sm text-tenue" key={i}>
                  Te propone añadir la sección «{bloques.find((b) => b.slug === a.bloque)?.etiqueta ?? a.bloque}»{a.pagina ? ` a «${a.pagina}»` : ''}. Eso es contenido: se hace desde{' '}
                  <Link className="text-violeta hover:underline" to={`/e/${espacio}/s/${sitio.id}/paginas`}>
                    Páginas
                  </Link>
                  .
                </p>
              ))}
              {resultado.necesitaDato && <p className="mt-2 text-sm text-tenue">{resultado.necesitaDato}</p>}
              {resultado.creditos && (
                <p className="mt-2 text-sm text-tenue">
                  {actual?.estado === 'fallida' && actual.creditos === 0 ? 'No se te ha cobrado: no se pudo preparar.' : `Ha costado ${resultado.creditos.consumidos} créditos.`}
                </p>
              )}
            </section>
          )}

          {fase === 'previa' && versionActual && (
            <div className="flex flex-col gap-3 border-t border-linea pt-4">
              {enConflicto ? (
                <CajaDeConflicto
                  alComparar={() => actual && void comparar(actual)}
                  alDescartar={() => void descartar()}
                  alJuntar={() => void juntar()}
                  alRegenerar={regenerarSobreLaPublicada}
                  ocupado={ocupado || Boolean(catalogo?.congelado)}
                  publicada={laPublicada}
                  puedeRegenerar={Boolean(actual?.prompt)}
                />
              ) : (
                <div className="flex flex-wrap gap-2">
                  <Boton disabled={ocupado || versionActual === publicada || catalogo?.congelado} onClick={() => void descartar()}>
                    Descartar
                  </Boton>
                  <Boton
                    disabled={ocupado || catalogo?.congelado}
                    onClick={() => {
                      setFase('lista')
                      setDesde(null)
                      setMensaje({ texto: 'Lo siguiente que pidas parte de este cambio. Escribe el próximo.', tono: 'ok' })
                      areaDeTexto.current?.focus()
                    }}
                  >
                    Seguir modificando
                  </Boton>
                  <Boton
                    disabled={ocupado || versionActual === publicada || solicitada === versionActual || actual?.solicitud?.estado === 'pendiente' || catalogo?.congelado}
                    onClick={pedirPublicar}
                    tipo="primario"
                  >
                    {versionActual === publicada
                      ? 'Ya está publicada'
                      : puedePublicar
                        ? 'Publicar'
                        : solicitada === versionActual || actual?.solicitud?.estado === 'pendiente'
                          ? 'Solicitud enviada'
                          : 'Solicitar publicación'}
                  </Boton>
                </div>
              )}
              {!puedePublicar && actual?.solicitud?.estado === 'pendiente' && <p className="text-[13px] text-tenue">El equipo la está revisando. Te llegará un correo cuando la resuelva.</p>}
              {actual?.solicitud?.estado === 'rechazada' && (
                <Aviso tono="aviso">
                  El equipo no la ha publicado{actual.solicitud.comentario ? `: «${actual.solicitud.comentario}»` : '.'} Puedes seguir modificándola y volver a solicitarlo.
                </Aviso>
              )}
              {actual?.solicitud?.estado === 'aprobada' && versionActual !== publicada && <p className="text-[13px] text-tenue">El equipo la aprobó y la publicó; después se ha publicado otra.</p>}
            </div>
          )}
        </Tarjeta>

        <section aria-label="Previa del sitio" className="flex min-h-[32rem] flex-col overflow-hidden rounded-caja border border-linea bg-papel shadow-suave">
          {previaUrl ? (
            <>
              <div className="flex items-center justify-between gap-4 border-b border-linea bg-papel-2 px-4 py-2 text-[13px]">
                <span>
                  Previa de la versión {versionActual}
                  {versionActual === publicada ? ' (la publicada)' : ''}
                </span>
                <a className="font-medium text-violeta hover:underline" href={previaUrl} rel="noreferrer" target="_blank">
                  Abrir en una pestaña
                </a>
              </div>
              {contenido && sinPortada && (
                // La previa enseña lo publicado (§13): con la portada en borrador sale «Esta página no existe».
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-aviso/30 bg-aviso-suave px-4 py-2 text-[13px]">
                  {portadaEnBorrador(contenido) ? (
                    <>
                      <span>
                        {publicada > 0
                          ? 'Tu portada está en borrador: quien visita tu web ve «Esta página no existe», y la previa también.'
                          : `La previa enseña lo publicado, y ${fraseDePaginas(contenido.paginasEnBorrador)}: por eso sale «Esta página no existe».`}
                      </span>
                      <Boton disabled={ocupado} onClick={() => setDialogo('contenido')} pequeno>
                        {publicada > 0 ? 'Publicar la portada' : 'Publicar las páginas'}
                      </Boton>
                    </>
                  ) : (
                    <span>
                      {SIN_PAGINA_DE_INICIO}{' '}
                      <Link className="font-medium text-violeta hover:underline" to={`/e/${espacio}/s/${sitio.id}/paginas`}>
                        Ir a Páginas
                      </Link>
                    </span>
                  )}
                </div>
              )}
              <iframe className="min-h-[30rem] flex-1 border-0 bg-white" key={recargas} src={previaUrl} title={`Previa de la versión ${versionActual}`} />
            </>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 bg-papel-2/60 p-6 text-center text-sm text-tenue">
              <Eye className="size-8 text-linea-fuerte" />
              <p role={trabajando ? 'status' : undefined}>
                {trabajando ? 'Preparando la previa…' : 'Aquí verás la previa de cada versión: la que generes, o cualquiera del historial.'}
              </p>
            </div>
          )}
        </section>
      </div>

      {dialogo && (
        <DialogoDePublicacion
          alTerminar={alTerminarLaPublicacion}
          cerrar={() => setDialogo(null)}
          // La primera vez, el contenido va con el diseño (sin él, la web abriría vacía); después, se ofrece pero sin marcar.
          conContenido={dialogo === 'contenido' || publicada === 0}
          conDiseno={dialogo === 'diseno'}
          espacio={espacio}
          explicacion={
            dialogo === 'contenido'
              ? 'La previa enseña lo publicado: publica tus páginas para verlas aquí. El diseño lo publicas cuando quieras.'
              : publicada === 0
                ? 'Es la primera vez que tu web sale a internet: con el diseño va el contenido, para que no abra vacía.'
                : undefined
          }
          sitio={sitio.id}
          version={dialogo === 'diseno' ? versionActual : null}
        />
      )}

      {tarjetaDeComparacion}

      <ComponentesDelDiseno sitio={sitio.id} />

      {historial ? (
        <HistorialDeDiseno acciones={accionesDelHistorial} bloques={bloques} congelado={Boolean(catalogo?.congelado)} historial={historial} ocupado={ocupado || trabajando} versionActual={versionActual} />
      ) : (
        <Esqueleto filas={4} />
      )}

      {confirmar === 'revertir' && historial?.anterior && (
        <Confirmacion alConfirmar={() => void volverALaAnterior()} cerrar={() => setConfirmar(null)} confirmar="Volver a la anterior" ocupado={ocupado} titulo="¿Volver a la versión anterior?">
          <p>
            Tu web volverá a verse como con la versión {historial.anterior.numero} («{historial.anterior.comentario}»), en unos segundos. Lo que está publicado ahora no se pierde: sigue en el
            historial y puedes volver a publicarlo.
          </p>
        </Confirmacion>
      )}
    </>
  )
}
